import { create } from 'zustand';
import type { MemberImageConflict } from '../services/warbandMemberImageService';
import { getRememberedGoogleIdentity } from '../services/localStorageService';
import type { SyncEntityConflict } from '../services/syncManifest';

export interface GoogleUser {
  sub: string;
  name: string;
  email: string;
  picture: string;
}

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'success';
export type DriveConnectionStatus =
  | 'restoring'
  | 'connected'
  | 'reauth-required'
  | 'signed-out';

export interface DriveConflict {
  driveOnly: Array<{ id: string; name: string }>;
  localOnly: Array<{ id: string; name: string }>;
}

interface AuthStore {
  user: GoogleUser | null;
  accessToken: string | null;
  /** UTC timestamp at which the Drive access token expires. */
  tokenExpiresAt: Date | null;
  connectionStatus: DriveConnectionStatus;
  syncStatus: SyncStatus;
  syncError: string | null;
  lastSynced: Date | null;
  /** Set when initializeSync detects warbands missing from one side. */
  pendingConflict: DriveConflict | null;
  pendingImageConflicts: MemberImageConflict[];
  pendingSyncConflicts: SyncEntityConflict[];

  setUser: (user: GoogleUser | null) => void;
  setAccessToken: (token: string | null, expiresAt?: Date | null) => void;
  setConnectionStatus: (status: DriveConnectionStatus) => void;
  setSyncStatus: (status: SyncStatus, error?: string) => void;
  setPendingConflict: (conflict: DriveConflict | null) => void;
  setPendingImageConflicts: (conflicts: MemberImageConflict[]) => void;
  setPendingSyncConflicts: (conflicts: SyncEntityConflict[]) => void;
  signOut: () => void;
}

const rememberedIdentity = getRememberedGoogleIdentity();

export const useAuthStore = create<AuthStore>((set) => ({
  user: rememberedIdentity,
  accessToken: null,
  tokenExpiresAt: null,
  connectionStatus: rememberedIdentity ? 'restoring' : 'signed-out',
  syncStatus: 'idle',
  syncError: null,
  lastSynced: null,
  pendingConflict: null,
  pendingImageConflicts: [],
  pendingSyncConflicts: [],

  setUser: (user) => set({ user }),
  setAccessToken: (token, expiresAt) => set({ accessToken: token, tokenExpiresAt: expiresAt ?? null }),
  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),

  setSyncStatus: (syncStatus, error) =>
    set({
      syncStatus,
      syncError: error ?? null,
      ...(syncStatus === 'success' ? { lastSynced: new Date() } : {}),
    }),

  setPendingConflict: (conflict) => set({ pendingConflict: conflict }),
  setPendingImageConflicts: (pendingImageConflicts) => set({ pendingImageConflicts }),
  setPendingSyncConflicts: (pendingSyncConflicts) => set({ pendingSyncConflicts }),

  signOut: () =>
    set({
      user: null,
      accessToken: null,
      tokenExpiresAt: null,
      connectionStatus: 'signed-out',
      syncStatus: 'idle',
      syncError: null,
      lastSynced: null,
      pendingConflict: null,
      pendingImageConflicts: [],
      pendingSyncConflicts: [],
    }),
}));
